import { ReactComponent as CloseButton } from "assets/svg/outline-cross.svg";
import { ReactComponent as OutlinePlus } from "assets/svg/outline-plus.svg";
import { ReactComponent as OutlineTrash1 } from "assets/svg/outline-trash-1.svg";
import createParsedAvailableTimePeriod from "components/Dashboard/DoctorDashboard/utils/createParsedAvailableTimePeriod";
import { useCalendarTime } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/contexts/CalendarTimeContext";
import ConfirmModal from "components/confirm-modal";
import IconButton from "components/icon-button";
import { DAYS_OF_WEEK } from "constants/daysOfWeek";
import useToggleModal from "hooks/useToggleModal";
import { isEqual } from "lodash";
import messages from "messages";
import moment from "moment-timezone";
import { useCallback, useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import {
  SwipeAction,
  SwipeableList,
  SwipeableListItem,
  TrailingActions,
  Type,
} from "react-swipeable-list";
import "react-swipeable-list/dist/styles.css";
import getClasses from "utils/getClasses";
import { formatParsedAvailableTimePeriodToAvailableTimePeriod } from "../../../calendar-view/utils/formatParsedAvailableTimePeriodToAvailableTimePeriod";
import getParsedAvailableTimePeriods from "../../../calendar-view/utils/getParsedAvailableTimePeriods";
import styles from "./AvailabilitySchedule.module.scss";
import AvailabilityScheduleDay from "./AvailabilityScheduleDay";
import AvailabilityScheduleRange from "./AvailabilityScheduleRange";

interface AvailabilityScheduleProps {
  schedule?: AvailableTimePeriod;
  close: () => void;
  save: (updatedPeriod: AvailableTimePeriod) => void;
}

const EMPTY_TIME_SLOT: ScheduleInterval = {
  start_time: "",
  end_time: "",
};

const AvailabilitySchedule = ({
  close,
  schedule,
  save,
}: AvailabilityScheduleProps) => {
  const { currentCalendarTimezone } = useCalendarTime();

  const isNewSchedule = !schedule;
  const defaultSchedule = useMemo(
    () => createParsedAvailableTimePeriod(currentCalendarTimezone),
    [currentCalendarTimezone],
  );

  const initialPeriod = useMemo(
    () =>
      schedule
        ? getParsedAvailableTimePeriods([schedule], currentCalendarTimezone)[0]
        : defaultSchedule,
    [schedule, defaultSchedule, currentCalendarTimezone],
  );

  const [periodWorkingCopy, setPeriodWorkingCopy] = useState(initialPeriod);

  const startDateValue = useMemo(
    () => +new Date(periodWorkingCopy.start_date),
    [periodWorkingCopy.start_date],
  );

  const {
    show: showConfirmCancelModal,
    open: openConfirmCancelModal,
    close: closeConfirmCancelModal,
  } = useToggleModal(false);

  const isDirty = useMemo(
    () => !isEqual(periodWorkingCopy, initialPeriod),
    [periodWorkingCopy, initialPeriod],
  );

  const weekStartDateValue = useMemo(
    () =>
      moment
        .tz(periodWorkingCopy.start_date || Date.now(), currentCalendarTimezone)
        .startOf("week")
        .valueOf(),
    [periodWorkingCopy, currentCalendarTimezone],
  );

  const onDateRangeChange = useCallback((period: ParsedAvailableTimePeriod) => {
    setPeriodWorkingCopy(period);
  }, []);

  const onDayChange = useCallback(
    (updatedInterval: ScheduleInterval[] | null, index: number) => {
      setPeriodWorkingCopy((prev) => ({
        ...prev,
        intervals: [
          ...prev.intervals.slice(0, index),
          updatedInterval,
          ...prev.intervals.slice(index + 1),
        ],
      }));
    },
    [],
  );

  const requestClose = useCallback(() => {
    if (isDirty) {
      openConfirmCancelModal();
      return;
    }

    close();
  }, [isDirty, openConfirmCancelModal, close]);

  const onSaveButtonClick = useCallback(() => {
    save(
      formatParsedAvailableTimePeriodToAvailableTimePeriod(periodWorkingCopy),
    );
  }, [periodWorkingCopy, save]);

  const weekValues = useMemo(
    () =>
      periodWorkingCopy.intervals.map((el, i) => {
        const date = moment
          .tz(weekStartDateValue, currentCalendarTimezone)
          .add(i, "days");

        while (date.valueOf() < startDateValue) {
          date.add(1, "week");
        }
        return +date;
      }),
    [
      startDateValue,
      currentCalendarTimezone,
      periodWorkingCopy,
      weekStartDateValue,
    ],
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <OutlinePlus className={styles["header__icon"]} />
        <div className={styles["header__title"]}>
          {isNewSchedule ? "Create Availability" : "Edit Availability"}
        </div>
        <IconButton onClick={requestClose}>
          <CloseButton />
        </IconButton>
      </div>
      <div className={styles.body}>
        <AvailabilityScheduleRange
          timezone={currentCalendarTimezone}
          period={periodWorkingCopy}
          onChange={onDateRangeChange}
        />
        <div className={styles.days}>
          <label className={styles["weekly-hours-label"]}>
            {messages.setUpWeeklyHours}
          </label>
          <SwipeableList type={Type.IOS}>
            {periodWorkingCopy.intervals.map((intervalsByDay, i) => (
              <SwipeableListItem
                trailingActions={
                  <TrailingActions>
                    <SwipeAction
                      onClick={() =>
                        onDayChange(
                          [
                            ...(intervalsByDay || [EMPTY_TIME_SLOT]),
                            EMPTY_TIME_SLOT,
                          ],
                          i,
                        )
                      }
                    >
                      <div className={getClasses(styles.action, styles.add)}>
                        <OutlinePlus />
                      </div>
                    </SwipeAction>
                    <SwipeAction
                      onClick={() => {
                        if (!intervalsByDay || intervalsByDay.length < 2) {
                          onDayChange(null, i);
                          return;
                        }

                        onDayChange(intervalsByDay.slice(0, -1), i);
                      }}
                    >
                      <div className={getClasses(styles.action, styles.remove)}>
                        <OutlineTrash1 />
                      </div>
                    </SwipeAction>
                  </TrailingActions>
                }
              >
                <AvailabilityScheduleDay
                  key={DAYS_OF_WEEK[i]}
                  dateValue={weekValues[i]}
                  dayTitle={DAYS_OF_WEEK[i]}
                  intervals={intervalsByDay}
                  timezone={currentCalendarTimezone}
                  onChange={(updatedDay) => onDayChange(updatedDay, i)}
                />
              </SwipeableListItem>
            ))}
          </SwipeableList>
        </div>
      </div>
      <div className={styles.footer}>
        <Button variant={"outline-primary"} onClick={requestClose}>
          Cancel
        </Button>
        <Button variant={"primary"} onClick={onSaveButtonClick}>
          Save
        </Button>
      </div>

      <ConfirmModal
        labelTitle={messages.areYouSure}
        children={messages.ifYouClose}
        labelConfirm={"OK"}
        show={showConfirmCancelModal}
        onCancel={closeConfirmCancelModal}
        onConfirm={close}
      />
    </div>
  );
};

export default AvailabilitySchedule;
